/**
 * L'encart « votre réseau vous rémunère » — discret mais compréhensible.
 *
 * Tom (2026-09-15) : « pas trop mis en avant mais compréhensible dans
 * l'administration quand on est connecté ». Une ligne dans le fil, un clic, et
 * la fenêtre explique les trois commissions avec son exemple : 712 €/mois pour
 * cent personnes. Les chiffres viennent de BAREME_RESEAU_TEXTE — une seule
 * source, la même que le module parrainage.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Coins, Copy, Check, X } from 'lucide-react';
import { appelerApi } from '../../reseau.ts';
import { BAREME_RESEAU_TEXTE, euros, type MonParrainage } from './types.ts';

export function EncartParrainage({ compact = false }: { compact?: boolean }) {
  const [ouvert, setOuvert] = useState(false);
  const [parrainage, setParrainage] = useState<MonParrainage | null>(null);
  const [copie, setCopie] = useState(false);

  useEffect(() => {
    if (!ouvert || parrainage) return;
    void appelerApi<MonParrainage>('/api/parrainage/moi').then((r) => setParrainage(r.donnees));
  }, [ouvert, parrainage]);

  return (
    <>
      <button
        onClick={() => setOuvert(true)}
        className={`flex w-full items-center justify-between gap-3 rounded-[10px] border border-graphite px-4 text-left transition-colors hover:bg-[#262626] ${compact ? 'py-2.5' : 'py-3'}`}
      >
        <span className="flex min-w-0 items-center gap-3">
          <Coins size={16} strokeWidth={1.5} className="shrink-0 text-fumee" />
          <span className="min-w-0">
            <span className="block text-[14px] text-blanc">Votre réseau vous rémunère</span>
            {!compact && (
              <span className="block truncate text-[12px] text-fumee">
                5 % sur vos filleuls, 2 % sur leurs filleuls, 1 % quand vous closez — comment ça marche
              </span>
            )}
          </span>
        </span>
        <span className="shrink-0 text-[12px] text-fumee">En savoir plus</span>
      </button>

      {ouvert && (
        <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center">
          <div className="absolute inset-0 bg-void/80" onClick={() => setOuvert(false)} />
          <div className="relative max-h-[92vh] w-full max-w-[600px] overflow-y-auto rounded-t-[16px] border border-graphite bg-charbon p-5 sm:rounded-[14px] sm:p-7">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <p className="label-caps text-fumee">Intérêt commercial</p>
                <h2 className="mt-1 text-[22px] font-light text-blanc">Votre réseau vous rémunère</h2>
              </div>
              <button onClick={() => setOuvert(false)} aria-label="Fermer" className="text-fumee hover:text-blanc">
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>

            <p className="text-[14px] leading-relaxed text-fumee">
              Mettre des entrepreneurs en relation est une compétence qui vaut de l’argent. Sur EventMatch,
              elle se rémunère sur les abonnements que votre réseau souscrit — chaque mois, tant qu’ils paient.
            </p>

            <div className="mt-5 flex flex-col gap-3">
              {BAREME_RESEAU_TEXTE.map((b) => (
                <div key={b.role} className="rounded-[10px] border border-graphite p-4">
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="text-[15px] text-blanc">{b.role}</p>
                    <p className="text-match-gradient font-serif text-[24px] leading-none">{b.taux}</p>
                  </div>
                  <p className="mt-2 text-[13px] leading-relaxed text-fumee">{b.quoi}</p>
                  <p className="mt-2 text-[13px] text-blanc/80">{b.exemple}</p>
                </div>
              ))}
            </div>

            <div className="mt-5 rounded-[10px] bg-void p-4">
              <p className="text-[14px] text-blanc">
                Au total, avec cent personnes autour de vous, un revenu récurrent de{' '}
                <span className="text-match-gradient font-serif text-[22px]">712 €</span> par mois.
              </p>
              <p className="mt-1 text-[12px] leading-relaxed text-fumee">
                Les commissions sont enregistrées ici puis versées par virement ; elles ne partent pas
                automatiquement.
              </p>
            </div>

            <div className="mt-5 border-t border-graphite pt-5">
              <p className="label-caps text-fumee">Votre lien</p>
              {parrainage ? (
                <>
                  <div className="mt-2 flex items-center gap-2 rounded-[8px] border border-graphite px-3 py-2">
                    <span className="truncate text-[13px] text-blanc">{parrainage.url}</span>
                    <button
                      onClick={() => {
                        void navigator.clipboard.writeText(parrainage.url).catch(() => undefined);
                        setCopie(true);
                        setTimeout(() => setCopie(false), 2000);
                      }}
                      aria-label="Copier"
                      className="ml-auto shrink-0 text-fumee hover:text-blanc"
                    >
                      {copie ? <Check size={14} strokeWidth={1.5} /> : <Copy size={14} strokeWidth={1.5} />}
                    </button>
                  </div>
                  <p className="mt-2 text-[13px] text-fumee">
                    {parrainage.filleuls.length} filleul{parrainage.filleuls.length > 1 ? 's' : ''} ·{' '}
                    {euros(parrainage.gains.du_centimes)} à recevoir
                    {parrainage.gains.verse_centimes > 0 ? ` · ${euros(parrainage.gains.verse_centimes)} déjà versés` : ''}
                  </p>
                </>
              ) : (
                <p className="mt-2 text-[13px] text-fumee">Chargement…</p>
              )}
              <p className="mt-3 text-[13px] leading-relaxed text-fumee">
                Les inscrits de vos événements sont vos filleuls automatiquement. Le QR, le détail et vos
                commissions sont dans{' '}
                <Link to="/compte/influence#parrainage" onClick={() => setOuvert(false)} className="text-blanc underline">Influence</Link>.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
